import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { version } from '../../../../package.json';
import { KeyboardShortcutsService } from '../../services/keyboard-shortcuts.service';
import { LayoutService } from '../../services/layout.service';
import { RoutingService } from '../../services/routing.service';
import {
  SEARCH_DIALOG_PANEL_CLASS,
  SearchDialogComponent,
} from '../global-search/search-dialog/search-dialog.component';
import { MapComponent } from '../map/map.component';
import { Note, ReleaseNoteComponent } from '../release-note/release-note.component';
import { AsideNavigationComponent } from './aside-navigation/aside-navigation.component';

@Component({
  selector: 'global-layout',
  templateUrl: './global-layout.component.html',
  styleUrl: './global-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AsideNavigationComponent, RouterModule, MapComponent],
})
export class GlobalLayoutComponent implements OnInit, OnDestroy {
  public layoutService = inject(LayoutService);
  private _routingService = inject(RoutingService);
  private _http = inject(HttpClient);
  private _dialog = inject(MatDialog);
  private _keyboard = inject(KeyboardShortcutsService);

  public $isMobile = this._routingService.$isMobile;
  private _destroy$ = new Subject<void>();
  private _isSearchDialogOpen = false;

  private _shortcut = this._keyboard.isMac() ? 'meta.k' : 'control.k';

  ngOnInit(): void {
    const newVersion = JSON.parse(localStorage.getItem('newVersion'));
    const lastReadVersion = localStorage.getItem('lastReadVersion');
    const versionWithoutPatchNumber = version.split('.').slice(0, 2).join('.');
    if (
      ((newVersion && lastReadVersion !== versionWithoutPatchNumber) || !lastReadVersion) &&
      !this.$isMobile()
    ) {
      this.showReleaseNotes(versionWithoutPatchNumber);
    }

    this._keyboard
      .addShortcut(this._shortcut)
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe(() => {
        if (!this._isSearchDialogOpen) {
          this._openSearchDialog();
        } else {
          this._dialog.closeAll();
        }
      });
  }

  private showReleaseNotes(versionWithoutPatchNumber: string): void {
    const filePath = `assets/releases-notes/${versionWithoutPatchNumber}.json`;
    this._http.get<{ notes: Note[] }>(filePath).subscribe({
      next: file => {
        const dialogRef = this._dialog.open(ReleaseNoteComponent, {
          maxWidth: this._routingService.$isMobile() ? '95vw' : '90vw',
          maxHeight: this._routingService.$isMobile() ? '95vh' : '90vh',
          width: this._routingService.$isMobile() ? '100vw' : '55%',
          height: 'auto',
          data: file.notes,
        });
        dialogRef
          .afterClosed()
          .pipe(takeUntil(this._destroy$))
          .subscribe(() => {
            localStorage.setItem('newVersion', 'false');
            localStorage.setItem('lastReadVersion', versionWithoutPatchNumber);
          });
      },
      error: err => {
        console.error('Unable to find release notes', {
          version,
          error: err,
          requestedPath: filePath,
        });
        localStorage.setItem('newVersion', 'false');
        localStorage.setItem('lastReadVersion', versionWithoutPatchNumber);
      },
    });
  }

  private _openSearchDialog(): void {
    if (this._isSearchDialogOpen) return;
    this._isSearchDialogOpen = true;

    const dialogRef = this._dialog.open(SearchDialogComponent, {
      width: '70vw',
      maxWidth: '90vw',
      maxHeight: '80vh',
      position: { top: '10vh' },
      panelClass: SEARCH_DIALOG_PANEL_CLASS,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._isSearchDialogOpen = false;
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
